import { useEffect, useRef, useState } from "react";
import { FaPlay } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { Modal } from "antd";
import { addMusicToPlayer } from "redux/AudioPlayerRedux";
import MusicService from "services/music/MusicService";
import TrackDetails from "./trackPage/TrackDetails";
import LikeService from "services/music/LikeService";
import { HiDotsHorizontal } from "react-icons/hi";
import PlaylistService from "services/music/PlaylistService";
import CreatePlaylistIcon from "./audioPlayer/AudioPlayer3/CreatePlaylistIcon/CreatePlaylistIcon";
import AddSongToFav from "./shared/AddSongToFav";
import { RouteName } from "constant/routeNames";
import { Helmet } from "react-helmet-async";
import TrackPageArtistRow from "./shared/TrackPageArtistRow";
import ArtistSuggestions from "./shared/ArtistSuggestions";
import MusicTractPageSkeleton from "components/skeleton/MusicTractPageSkeleton";
import AddSongToQueue from "./shared/AddSongToQueue";
import StickyMediaHeader from "components/shared/StickyMediaHeader";

function MusicTrackPage() {
  const dispatch = useDispatch<any>();
  const { id } = useParams();
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);

  const [trackData, setTrackData] = useState<any>();
  const [favorite, setFavorite] = useState<any>([]);
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const FE_URL = process.env.REACT_APP_FRONTEND_URL;
  const [isLoading, setIsLoading] = useState<any>(false);
  const isPremiumContent = trackData?.plan_id === 2;
  const isPlusUser = loggedInUser?.is_subscribed;
  const heroSentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (loggedInUser) {
      LikeService.getFavoriteById(loggedInUser?.id, loggedInUser?.id)
        .then((res) => {
          const favoritesIds = res?.profile?.loved?.data?.map(
            (item: any) => item?.id,
          );
          setFavorite(favoritesIds);
        })
        .catch(() => {});
    }
  }, [loggedInUser, loggedInUser?.id]);

  useEffect(() => {
    setIsLoading(true);
    MusicService.getTrackDataByID(id).then((res) => {
      setIsLoading(false);
      setTrackData(res);
    });
  }, [id]);

  const handleMusicPlayer = async () => {
    if (isPremiumContent && !isPlusUser) {
      setIsModalOpen(true);
      return;
    }
    dispatch(addMusicToPlayer(trackData));
  };

  useEffect(() => {
    if (loggedInUser) {
      PlaylistService.getUserPlaylists().then((res) => {
        setUserPlaylists(res.data.data);
      });
    }
  }, [loggedInUser]);

  return (
    <>
      <StickyMediaHeader
        sentinelRef={heroSentinelRef}
        image={trackData?.artwork_url}
        title={trackData?.title?.replace(/_/g, "'")}
        subtitle={trackData?.artists?.[0]?.name}
        onPlay={handleMusicPlayer}
        playLabel={isPremiumContent && !isPlusUser ? "Subscribe to Play" : "Play"}
      />
      <Helmet>
        <title>
          {trackData?.title ? `${trackData?.title} - AYOZAT Music` : "AYOZAT"}
        </title>
        <meta name="title" content={`${trackData?.title} - AYOZAT Music`} />
        <meta name="description" content={trackData?.title} />
        <link
          rel="canonical"
          href={`${FE_URL}/music/track/${trackData?.id || ""}`}
        />

        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`${trackData?.title} - AYOZAT Music`}
        />
        <meta property="og:description" content={trackData?.artists[0]?.name} />
        <meta property="og:image" content={trackData?.artwork_url} />
        <meta
          property="og:url"
          content={`${FE_URL}/music/track/${trackData?.id || ""}`}
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${trackData?.title} - AYOZAT Music`}
        />
        <meta
          name="twitter:description"
          content={trackData?.artists[0]?.name}
        />
        <meta name="twitter:image" content={trackData?.artwork_url} />
        <meta
          name="twitter:url"
          content={`${FE_URL}/music/track/${trackData?.id || ""}`}
        />
      </Helmet>

      {!isLoading && trackData ? (
        <>
          {/* hero details */}
          <TrackDetails data={trackData && trackData} sentinelRef={heroSentinelRef} />

          <div className="mt-6 px-1 max-sm:px-0">
            {/* play + actions */}
            <div className="flex items-center gap-3">
              <button
                className={`button-red flex items-center justify-center gap-2 rounded-full px-7 py-3 text-center font-semibold text-white shadow-lg shadow-[var(--red)]/30 transition-all duration-300 hover:scale-[1.03] hover:shadow-xl max-sm:ml-2 max-sm:px-5 max-sm:text-[15px]`}
                onClick={handleMusicPlayer}
              >
                <FaPlay className="text-[13px]" />
                {isPremiumContent && !isPlusUser ? "Subscribe to Play" : "Play"}
              </button>

              <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white backdrop-blur-sm transition-all duration-300 hover:border-white/20 hover:bg-white/[0.1]">
                <AddSongToQueue
                  song={trackData}
                  toolTiId={`new-released-card`}
                  Icon={true}
                />
              </div>

              <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white backdrop-blur-sm transition-all duration-300 hover:border-white/20 hover:bg-white/[0.1]">
                <AddSongToFav song={trackData} favorite={favorite} Icon={true} />
              </div>

              <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white backdrop-blur-sm transition-all duration-300 hover:border-white/20 hover:bg-white/[0.1]">
                <CreatePlaylistIcon
                  userPlaylists={userPlaylists}
                  setUserPlaylists={setUserPlaylists}
                  mediaId={id}
                  Icon={HiDotsHorizontal}
                />
              </div>
            </div>

            <div className="my-8 h-px w-full bg-gradient-to-r from-white/15 via-white/5 to-transparent" />

            {/* artist details */}
            {trackData?.artists?.map((artist: any, i: number) => (
              <TrackPageArtistRow artist={artist} key={i} />
            ))}

            <div className="my-8">
              {trackData?.artists?.map((artist: any, i: number) => (
                <ArtistSuggestions
                  artist={artist}
                  key={i}
                  trackId={trackData?.id}
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        <></>
      )}

      <Modal
        title={null}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        centered
        className="premium-modal"
        style={{ border: "1px solid #333" }}
        bodyStyle={{ backgroundColor: "#111", padding: 0 }}
      >
        <div className="flex flex-col items-center justify-center bg-[#111] p-6 text-center">
          <div className="mb-4 rounded-full bg-[var(--red)]/10 p-4">
            <FaPlay className="text-4xl text-[var(--red)]" />
          </div>
          <h3 className="mb-2 text-2xl font-bold text-white">
            Premium Content
          </h3>
          <p className="mb-6 text-gray-400">
            This track is exclusively available for premium subscribers. Upgrade
            your plan to gain full access.
          </p>
          <div className="flex w-full gap-4">
            <button
              onClick={() => setIsModalOpen(false)}
              className="flex-1 rounded-lg border border-gray-600 px-4 py-2 font-semibold text-gray-300 transition hover:bg-[#222]"
            >
              Maybe Later
            </button>
            <Link
              to={RouteName.SubscriptionPlanRoute}
              className="flex flex-1 items-center justify-center rounded-lg bg-[var(--red)] px-4 py-2 font-semibold text-white shadow-md transition hover:opacity-90"
            >
              View Plans
            </Link>
          </div>
        </div>
      </Modal>

      {isLoading && <MusicTractPageSkeleton />}
    </>
  );
}

export default MusicTrackPage;